import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Crown, User } from 'lucide-react';

interface UserPremiumManagerProps {
  userId: string;
  userName: string;
  userEmail: string;
  subscriptionTier: 'free' | 'premium';
  subscriptionExpiresAt: string | null;
  onUpdate: () => void;
}

type DurationOption = '12' | '6' | '3' | 'none';

export function UserPremiumManager({
  userId,
  userName,
  userEmail,
  subscriptionTier,
  subscriptionExpiresAt,
  onUpdate,
}: UserPremiumManagerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [duration, setDuration] = useState<DurationOption>('12');
  const [isLoading, setIsLoading] = useState(false);

  const isPremium = subscriptionTier === 'premium';

  const getStatusBadge = () => {
    if (isPremium) {
      return (
        <Badge variant="outline" className="gap-1 border-primary text-primary">
          <Crown className="h-3 w-3" /> Premium
          {subscriptionExpiresAt &&
            ` hasta ${new Date(subscriptionExpiresAt).toLocaleDateString('es-ES')}`}
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="gap-1 text-muted-foreground">
        <User className="h-3 w-3" /> Free
      </Badge>
    );
  };

  const callSetPremium = async (
    body:
      | { userId: string; tier: 'premium'; plan: 'monthly'; months?: number }
      | { userId: string; tier: 'free' },
  ) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-set-premium', {
        body,
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success(
        body.tier === 'premium'
          ? 'Premium activado para el usuario'
          : 'Usuario revertido a Free',
      );
      setIsOpen(false);
      onUpdate();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al actualizar la suscripción';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGrant = () => {
    callSetPremium({
      userId,
      tier: 'premium',
      plan: 'monthly',
      ...(duration !== 'none' ? { months: parseInt(duration, 10) } : {}),
    });
  };

  const handleRevoke = () => {
    callSetPremium({ userId, tier: 'free' });
  };

  return (
    <>
      <div className="flex items-center gap-2">
        {getStatusBadge()}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsOpen(true)}
          className="text-muted-foreground hover:text-foreground"
        >
          <Crown className="h-4 w-4" />
        </Button>
      </div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Gestionar Premium</DialogTitle>
            <DialogDescription>
              {userName} ({userEmail})
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Estado actual: {getStatusBadge()}
            </p>

            {!isPremium && (
              <div className="space-y-2">
                <Label>Vigencia</Label>
                <Select
                  value={duration}
                  onValueChange={(v) => setDuration(v as DurationOption)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="12">12 meses</SelectItem>
                    <SelectItem value="6">6 meses</SelectItem>
                    <SelectItem value="3">3 meses</SelectItem>
                    <SelectItem value="none">Sin vencimiento</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Activación manual: no crea una suscripción en Stripe. El plan queda como Premium mensual.
                </p>
              </div>
            )}

            {isPremium && (
              <p className="text-sm text-muted-foreground">
                Este usuario tiene Premium activo. Puedes revertirlo a Free. Si tiene una
                suscripción real en Stripe, hazlo mejor desde Stripe para no descuadrar el cobro.
              </p>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancelar
            </Button>
            {isPremium ? (
              <Button variant="destructive" onClick={handleRevoke} disabled={isLoading}>
                {isLoading ? 'Procesando...' : 'Revertir a Free'}
              </Button>
            ) : (
              <Button onClick={handleGrant} disabled={isLoading}>
                {isLoading ? 'Procesando...' : 'Activar Premium'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
